import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  PhoneCall,
  Activity,
  Eye,
  Headphones,
  Phone,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import useAppStore from '../../store/appStore';

const formatDuration = (seconds) => {
  if (!seconds) return '0m 0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

// Defined outside Dashboard so React never re-creates it as a new component type on each render
const KPICard = ({ title, value, change, isPositive, icon: Icon, colorClass, growthLabel }) => (
  <div className="controlled-glass rounded-[24px] sm:rounded-xl p-6 sm:p-3.5 flex flex-col justify-between h-full inner-highlight group hover:border-white/20 transition-all duration-300">
    <div className="flex justify-between items-start mb-6 sm:mb-3">
      <div className="flex items-center gap-4 sm:gap-2.5">
        <div className={`p-3 sm:p-1.5 rounded-2xl sm:rounded-lg bg-white/5 border border-white/10 group-hover:bg-white/10 transition-colors ${colorClass}`}>
          <Icon className="h-6 w-6 sm:h-4 sm:w-4 text-white/70" />
        </div>
        <span className="text-[11px] sm:text-[9px] font-bold text-white/40 uppercase tracking-[0.2em]">{title}</span>
      </div>
    </div>

    <div className="flex flex-col gap-1">
      <h3 className="text-3xl sm:text-[18px] font-bold text-white tracking-tight">{value}</h3>
      <div className="flex items-center gap-3 mt-3 sm:mt-1.5">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-2 sm:py-0.5 rounded-xl sm:rounded-lg text-[11px] sm:text-[9px] font-bold tracking-wider ${isPositive ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {change}%
        </div>
        <span className="text-[10px] sm:text-[8px] text-white/20 font-bold uppercase tracking-widest italic">{growthLabel}</span>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { t } = useLanguage();
  const { dashboardData, dashboardLoading, fetchDashboardAnalysis } = useAppStore((s) => ({
    dashboardData: s.dashboardData,
    dashboardLoading: s.dashboardLoading,
    fetchDashboardAnalysis: s.fetchDashboardAnalysis,
  }));

  useEffect(() => {
    fetchDashboardAnalysis();
  }, [fetchDashboardAnalysis]);

  const loading = dashboardLoading && !dashboardData;
  const dash = dashboardData?.dashboard || {};
  const growth = dash.growth_vs_last_month || {};
  const agents = dashboardData?.your_ai_agents?.test_speed || [];
  const activity = dashboardData?.recent_activity || [];

  const quickActions = [
    { title: t('createNewAgent'), href: '/agents/create', icon: Plus, color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20', iconBg: 'bg-indigo-500/20' },
    { title: t('viewCallLogs'), href: '/call-logs', icon: PhoneCall, color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', iconBg: 'bg-blue-500/20' },
    { title: t('agentTraining'), href: '/training', icon: Activity, color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', iconBg: 'bg-emerald-500/20' },
    { title: t('viewAnalytics'), href: '/analytics', icon: Eye, color: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20', iconBg: 'bg-fuchsia-500/20' },
  ];

  return (
    <div className="space-y-8 pb-12">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard
          title={t('totalCalls')}
          value={loading ? '—' : (dash.total_calls ?? 0).toLocaleString()}
          change={growth.total_calls_percent ?? 0}
          isPositive={(growth.total_calls_percent ?? 0) >= 0}
          icon={Phone}
          growthLabel={t('growthVsLastMonth')}
        />
        <KPICard
          title={t('successRate')}
          value={loading ? '—' : `${dash.success_rate ?? 0}%`}
          change={growth.success_rate_percent ?? 0}
          isPositive={(growth.success_rate_percent ?? 0) >= 0}
          icon={Activity}
          growthLabel={t('growthVsLastMonth')}
        />
        <KPICard
          title={t('avgCallDuration')}
          value={loading ? '—' : formatDuration(dash.avg_call_duration)}
          change={growth.avg_call_duration_percent ?? 0}
          isPositive={(growth.avg_call_duration_percent ?? 0) >= 0}
          icon={Activity}
          growthLabel={t('growthVsLastMonth')}
        />
        <KPICard
          title={t('activeAgents')}
          value={loading ? '—' : (dash.active_agents ?? 0)}
          change={growth.active_agents_growth_percent ?? 0}
          isPositive={(growth.active_agents_growth_percent ?? 0) >= 0}
          icon={Users}
          growthLabel={t('growthVsLastMonth')}
        />
      </div>

      {/* Middle Row: Agents & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Your AI Agents */}
        <div className="controlled-glass rounded-[32px] p-7 sm:p-5 inner-highlight">
          <div className="flex justify-between items-center mb-8 sm:mb-6">
            <h2 className="text-lg sm:text-base font-bold text-white tracking-tight">{t('yourAiAgents')}</h2>
            <Link to="/agents" className="text-[10px] sm:text-[9px] font-bold text-primary-400 uppercase tracking-widest hover:text-primary-300 transition-colors">
              {t('viewAll')}
            </Link>
          </div>
          <div className="space-y-3 sm:space-y-1.5">
            {loading ? (
              <p className="text-xs text-white/20 italic">{t('loading') || 'Loading...'}</p>
            ) : agents.length === 0 ? (
              <p className="text-xs text-white/20 italic">No agents yet.</p>
            ) : (
              agents.slice(0, 5).map((agent, idx) => (
                <div key={idx} className="flex items-center justify-between p-5 sm:p-2.5 bg-white/[0.03] border border-white/10 rounded-[24px] sm:rounded-xl group hover:bg-white/[0.05] transition-all">
                  <div className="flex items-center gap-5 sm:gap-3.5">
                    <div className="w-12 h-12 sm:w-9 sm:h-9 rounded-xl sm:rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
                      <Headphones className="h-6 w-6 sm:h-4 sm:w-4 text-primary-400/70" />
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-[13px] font-bold text-white tracking-tight">{agent.agent_name}</h4>
                      <p className="text-xs sm:text-[10px] font-medium text-white/20 mt-0.5 italic">
                        {agent.calls} {t('calls')} • {agent.success_rate}% {t('successRate')}
                      </p>
                    </div>
                  </div>
                  <span className={`px-4 py-1.5 sm:px-2.5 sm:py-0.5 rounded-lg sm:rounded-md text-[9px] sm:text-[8px] font-bold uppercase tracking-widest ${agent.active ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.1)]' : 'bg-white/5 border border-white/10 text-white/30'}`}>
                    {agent.active ? t('active') : t('inactive') || 'Inactive'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="controlled-glass rounded-[32px] p-7 sm:p-5 inner-highlight">
          <div className="flex justify-between items-center mb-8 sm:mb-6">
            <h2 className="text-lg sm:text-base font-bold text-white tracking-tight">{t('recentActivity')}</h2>
          </div>
          <div className="space-y-6 sm:space-y-4">
            {loading ? (
              <p className="text-xs text-white/20 italic">{t('loading') || 'Loading...'}</p>
            ) : activity.length === 0 ? (
              <p className="text-xs text-white/20 italic">No recent activity.</p>
            ) : (
              activity.map((item, idx) => (
                <div key={idx} className="flex items-start justify-between group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                      <Phone className="h-4 w-4 text-white/40 group-hover:text-white/70 transition-colors" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-white tracking-tight">{item.agent_name}</h4>
                      <p className="text-[10px] font-medium text-white/20 mt-0.5 italic">{item.activity}</p>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-white/20 italic mt-1.5 whitespace-nowrap">
                    {item.created_label}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="controlled-glass rounded-[32px] p-7 sm:p-5 inner-highlight">
        <h2 className="text-lg sm:text-base font-bold text-white tracking-tight mb-8 sm:mb-6">{t('quickActions')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-4">
          {quickActions.map((action) => (
            <Link key={action.title} to={action.href} className={`flex items-center gap-4 sm:gap-2.5 p-5 sm:p-2.5 rounded-[24px] sm:rounded-xl border transition-all hover:scale-[1.02] active:scale-[0.98] ${action.color}`}>
              <div className={`w-10 h-10 sm:w-8 sm:h-8 rounded-xl sm:rounded-lg flex items-center justify-center ${action.iconBg}`}>
                <action.icon className="h-5 w-5 sm:h-4 sm:w-4" />
              </div>
              <span className="text-xs sm:text-[9px] font-bold uppercase tracking-widest text-left leading-tight">
                {action.title}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
